@if (total() === 0) {
  <div class="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-200 py-14 text-center text-neutral-400">
    <mat-icon class="text-4xl!">check_circle</mat-icon>
    <p class="text-base">{{ "guaranteeV2.message.deliveryEmpty" | translate }}</p>
  </div>
} @else {
  <div class="flex flex-col gap-3">
    <!--
      Two sections rather than one list: a member who never booked and an attempt that fell through
      are chased differently, and a single count would hide which of the two this window is full of.
    -->
    @if (gaps().neverPlanned.length > 0) {
      <section class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-2 px-1">
          <mat-icon
            class="h-4! w-4! text-base! leading-4! text-neutral-400"
            [matTooltip]="'guaranteeV2.message.deliveryNeverPlannedHint' | translate"
            >radio_button_unchecked</mat-icon
          >
          <span class="text-sm font-semibold text-neutral-500">{{ "guaranteeV2.label.deliveryNeverPlanned" | translate }}</span>
          <guarantees-chip variant="outline">{{ gaps().neverPlanned.length }}</guarantees-chip>
        </div>

        @for (row of gaps().neverPlanned; track row.key) {
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-neutral-200 bg-neutral-0 px-2 py-1.5">
            <span class="inline-flex shrink-0 items-center gap-1 text-sm text-neutral-500 tabular-nums">
              <span class="font-medium text-neutral-700">{{ row.visitDate | date: "d MMM yyyy" }}</span>
              @if (row.waitingDays > 0) {
                <guarantees-chip
                  variant="outline"
                  icon="hourglass_bottom"
                  [tooltip]="'guaranteeV2.message.waitingDaysHint' | translate: { count: row.waitingDays }"
                  >{{ row.waitingDays }}{{ "guaranteeV2.label.daysShort" | translate }}</guarantees-chip
                >
              }
            </span>

            <mat-icon
              class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
              [matTooltip]="kindLabelKeys[row.line.prescription.kind] | translate"
              >{{ kindIcons[row.line.prescription.kind] }}</mat-icon
            >

            <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800">{{ row.line.prescription.name }}</span>

            @if (row.diagnosis; as diagnosis) {
              <span class="max-w-64 min-w-0 shrink-0">
                <guarantees-diagnosis-chip
                  [diagnosis]="diagnosis"
                  [showName]="false"
                  [showCaseState]="false"
                ></guarantees-diagnosis-chip>
              </span>
            }

            <guarantees-chip
              class="shrink-0"
              tone="warning"
              icon="verified"
              [tooltip]="'guaranteeV2.message.deliveryNeverPlannedHint' | translate"
              >{{ "guaranteeV2.label.fulfillmentNotPlanned" | translate }}</guarantees-chip
            >

            <button
              class="ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none sm:size-6"
              type="button"
              [attr.aria-label]="'guaranteeV2.message.openInStreamHint' | translate"
              [matTooltip]="'guaranteeV2.message.openInStreamHint' | translate"
              (click)="guaranteeOpened.emit(row.guaranteeId)"
            >
              <mat-icon class="h-4! w-4! text-base! leading-4!">open_in_new</mat-icon>
            </button>
          </div>
        }
      </section>
    }

    @if (gaps().stopped.length > 0) {
      <section class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-2 px-1">
          <mat-icon
            class="h-4! w-4! text-base! leading-4! text-error-500"
            [matTooltip]="'guaranteeV2.message.deliveryStoppedHint' | translate"
            >cancel</mat-icon
          >
          <span class="text-sm font-semibold text-neutral-500">{{ "guaranteeV2.label.deliveryStopped" | translate }}</span>
          <guarantees-chip variant="outline">{{ gaps().stopped.length }}</guarantees-chip>
        </div>

        @for (row of gaps().stopped; track row.key) {
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-error-200 bg-error-50/30 px-2 py-1.5">
            <span class="inline-flex shrink-0 items-center gap-1 text-sm text-neutral-500 tabular-nums">
              <span class="font-medium text-neutral-700">{{ row.visitDate | date: "d MMM yyyy" }}</span>
              @if (row.waitingDays > 0) {
                <guarantees-chip
                  variant="outline"
                  icon="hourglass_bottom"
                  [tooltip]="'guaranteeV2.message.waitingDaysHint' | translate: { count: row.waitingDays }"
                  >{{ row.waitingDays }}{{ "guaranteeV2.label.daysShort" | translate }}</guarantees-chip
                >
              }
            </span>

            <mat-icon
              class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
              [matTooltip]="kindLabelKeys[row.line.prescription.kind] | translate"
              >{{ kindIcons[row.line.prescription.kind] }}</mat-icon
            >

            <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800">{{ row.line.prescription.name }}</span>

            @if (row.diagnosis; as diagnosis) {
              <span class="max-w-64 min-w-0 shrink-0">
                <guarantees-diagnosis-chip
                  [diagnosis]="diagnosis"
                  [showName]="false"
                  [showCaseState]="false"
                ></guarantees-diagnosis-chip>
              </span>
            }

            <!-- Why it stopped is the whole point of this row: a no-show is chased, a decline is not. -->
            @if (row.stopReason; as reason) {
              <guarantees-chip
                class="shrink-0"
                tone="error"
                [icon]="stopReasonIcons[reason]"
                [tooltip]="stopReasonLabelKeys[reason] | translate"
                >{{ stopReasonLabelKeys[reason] | translate }}</guarantees-chip
              >
            } @else {
              <guarantees-chip
                class="shrink-0"
                tone="error"
                icon="cancel"
                [tooltip]="'guaranteeV2.message.fulfillmentStoppedHint' | translate"
                >{{ "guaranteeV2.label.fulfillmentStopped" | translate }}</guarantees-chip
              >
            }

            <button
              class="ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none sm:size-6"
              type="button"
              [attr.aria-label]="'guaranteeV2.message.openInStreamHint' | translate"
              [matTooltip]="'guaranteeV2.message.openInStreamHint' | translate"
              (click)="guaranteeOpened.emit(row.guaranteeId)"
            >
              <mat-icon class="h-4! w-4! text-base! leading-4!">open_in_new</mat-icon>
            </button>
          </div>
        }
      </section>
    }
  </div>
}
